export const DEVICE_PLATFORMS = ["ANDROID_TV", "SAMSUNG_TIZEN", "WEB_IOS"] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

export const HOME_TARGETS = ["ALL", ...DEVICE_PLATFORMS] as const;
export type HomeTarget = (typeof HOME_TARGETS)[number];

export const DEVICE_KINDS = ["TV", "WEB_CHILD"] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

export const PLAYER_STATES = [
  "IDLE",
  "LOADING",
  "PLAYING",
  "PAUSED",
  "ENDED",
  "ERROR",
] as const;
export type PlayerState = (typeof PLAYER_STATES)[number];

export const DEVICE_COMMAND_TYPES = [
  "PAUSE",
  "RESUME",
  "STOP",
  "LOCK",
  "UNLOCK",
  "REFRESH",
  "BLOCK_VIDEO",
  "BLOCK_CHANNEL",
  "BLOCK_AND_STOP",
] as const;
export type DeviceCommandType = (typeof DEVICE_COMMAND_TYPES)[number];

export const DEVICE_COMMAND_STATUSES = [
  "PENDING",
  "DELIVERED",
  "ACKED",
  "FAILED",
  "EXPIRED",
] as const;
export type DeviceCommandStatus = (typeof DEVICE_COMMAND_STATUSES)[number];

export const KEYWORD_MATCH_TYPES = ["EXACT", "CONTAINS"] as const;
export type KeywordMatchType = (typeof KEYWORD_MATCH_TYPES)[number];

export interface ApiSuccess<T> {
  ok: true;
  data: T;
  requestId: string;
}

export interface ApiFailure {
  ok: false;
  error: {
    code: string;
    message: string;
    details?: Readonly<Record<string, unknown>>;
  };
  requestId: string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export interface VideoSummary {
  videoId: string;
  title: string;
  channelId: string;
  channelName: string;
  thumbnailUrl: string;
  durationSeconds: number;
  embeddable: boolean;
  publishedAt?: string;
  progressSeconds?: number;
}

export interface ChildAccessPolicy {
  allowed: boolean;
  locked: boolean;
  scheduleAllowed: boolean;
  dailyLimitReached: boolean;
  usedTodaySeconds: number;
  dailyLimitMinutes: number | null;
  message: string | null;
}

export interface LibraryPayload {
  policy: ChildAccessPolicy;
  favorites: VideoSummary[];
  continueWatching: VideoSummary[];
  recentlyWatched: VideoSummary[];
  parentAdded: VideoSummary[];
}

export interface FavoriteMutationResult {
  contentId: string;
  favorite: boolean;
  item?: VideoSummary;
}

export type ChildProfileStatus =
  | "AVAILABLE"
  | "LOCKED"
  | "OUTSIDE_SCHEDULE"
  | "DAILY_LIMIT_REACHED";

export interface ChildScheduleWindow {
  startMinute: number;
  endMinute: number;
}

export interface ProfileSummary {
  profileId: string;
  name: string;
  avatarKey: string;
  watchSecondsToday: number;
  watchMinutesToday: number;
  remainingMinutes: number | null;
  dailyLimitMinutes: number | null;
  usageProgress: number;
  scheduleToday: ChildScheduleWindow[];
  status: ChildProfileStatus;
  allowed: boolean;
  message: string | null;
  watchedVideoCount: number;
  favoritesCount: number;
}

export interface HomeSection {
  id: string;
  title: string;
  slug: string;
  sortOrder: number;
  enabled: boolean;
  target: HomeTarget;
  /** true khi Parent đánh dấu section này là Series (episodes theo thứ tự). */
  isSeries: boolean;
  videos: VideoSummary[];
}

export interface DeviceSummary {
  id: string;
  name: string;
  platform: DevicePlatform;
  deviceKind: DeviceKind;
  model: string;
  osVersion: string;
  online: boolean;
  locked: boolean;
  lastSeenAt: string | null;
  dailyLimitMinutes: number | null;
  usedTodaySeconds: number;
  playback: CurrentPlayback | null;
}

export interface CurrentPlayback {
  videoId: string | null;
  title: string | null;
  thumbnailUrl: string | null;
  channelId: string | null;
  channelName: string | null;
  positionSeconds: number;
  durationSeconds: number;
  state: PlayerState;
  updatedAt: string;
}

export interface PlaybackReport {
  videoId: string | null;
  title: string | null;
  thumbnailUrl: string | null;
  channelId: string | null;
  channelName: string | null;
  positionSeconds: number;
  durationSeconds: number;
  state: PlayerState;
  occurredAt: string;
}

export interface DeviceCommand {
  id: string;
  type: DeviceCommandType;
  payload: Readonly<Record<string, unknown>>;
  status: DeviceCommandStatus;
  createdAt: string;
  expiresAt: string;
}

export interface PairingStartRequest {
  platform: DevicePlatform;
  deviceKind?: DeviceKind;
  model: string;
  osVersion: string;
  appVersion: string;
}

export interface PairingStartResponse {
  pairingId: string;
  code: string;
  pollingSecret: string;
  expiresAt: string;
  pollAfterSeconds: number;
}

export interface PairingStatusResponse {
  status: "PENDING" | "PAIRED" | "EXPIRED";
  deviceId?: string;
  deviceToken?: string;
  apiBaseUrl?: string;
}

export interface SessionResponse {
  parent: {
    id: string;
    email: string;
  };
  csrfToken: string;
  expiresAt: string;
}

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

export interface ParentSearchResult extends VideoSummary {
  filterStatus: "ALLOWED" | "BLOCKED_SHORT" | "BLOCKED_RULE" | "NOT_EMBEDDABLE";
  filterReason: string | null;
}
